<template>
    <div>
        <div class="grid grid-cols-12 gap-4">
            <div class="col-span-12">
                <!-- LOUNGE WIZARD SEARCH -->
                <div
                    class="overflow-visible rounded-lg col-span-12 flex flex-col justify-between mb-7 lg:pr-8 px-2 relative"
                >
                    <LoungeWizardSearch />
                </div>
                <!-- SEARCH FILTER BAR -->
                <LoungesFilterBar
                    v-if="searchState !== 'loading'"
                    class="col-span-12 mb-7"
                    @toggleMap="toggleOpenMap"
                />
                <div id="top" class="hidden"></div>
                <!-- LOADING STATE -->
                <div v-if="searchState === 'loading'" class="col-span-12">
                    <LoadingState :loading="true" class="relative mt-16" />
                </div>

                <!-- RESULTS -->
                <div v-else class="col-span-12">
                    <LoungeSearchResultsHeader />
                    <div class="grid grid-cols-12 gap-4">
                        <div
                            :class="{
                                'col-span-12': !showMap,
                                'col-span-6': showMap,
                            }"
                        >
                            <LoungesSearchResults />
                            <LoungeFilterModal />
                        </div>
                        <div
                            class="fixed inset-0 z-[999] top-0 left-0"
                            v-if="showMap"
                        >
                            <Teleport to="body">
                                <Suspense>
                                    <Map
                                        :loading="loading"
                                        :showMap="showMap"
                                        :mapMarkers="mapMarkers"
                                        :mapType="mapType"
                                        :mapCenter="mapCenter"
                                        @closeMap="handleCloseMap"
                                    >
                                        <template #actionbar>
                                            <div
                                                class="absolute gap-2 top-16 md:w-1/2 w-5/6 md:top-6 md:left-6 left-3 z-[9999] flex flex-col overflow-visible"
                                                style="
                                                    transform: translate3d(
                                                        0,
                                                        0,
                                                        200px
                                                    );
                                                "
                                            >
                                                <UiSingleSelect
                                                    :label="localTripLeg.name"
                                                    :options="
                                                        tripAirports.airports
                                                    "
                                                    v-model="localTripLeg"
                                                    @update:modelValue="
                                                        changeTripLeg
                                                    "
                                                    :showCounter="false"
                                                    labelKey="name"
                                                    valueKey="code"
                                                    class="grow"
                                                />

                                                <LoungesFilterBar
                                                    v-if="searchResultsCount"
                                                    @toggleMap="toggleOpenMap"
                                                    class="relative z-10"
                                                />
                                                <EmptyState
                                                    v-else-if="
                                                        searchState ===
                                                            'empty' ||
                                                        searchState === 'error'
                                                    "
                                                    title="No lounges found"
                                                    description="No lounge access found for this leg of your trip. Try changing your search parameters."
                                                    :hasAction="false"
                                                    class="relative bg-white rounded-lg shadow"
                                                >
                                                    <template #image>
                                                        <div
                                                            class="w-16 h-16 bg-slate-200 rounded-full justify-center items-center flex"
                                                        >
                                                            <Icon
                                                                icon="search"
                                                                svgClass="w-8 h-8"
                                                            />
                                                        </div>
                                                    </template>
                                                </EmptyState>
                                            </div>
                                        </template>
                                        <template
                                            #popup="{
                                                closeHandler,
                                                infoWindowContent,
                                                show,
                                            }"
                                        >
                                            <LoungeInfoWindow
                                                :infoWindowContent="
                                                    infoWindowContent
                                                "
                                                :show="show"
                                                @close="closeHandler"
                                                @favoriteChanged="
                                                    handleFavoriteChanged
                                                "
                                                class="z-[9999]"
                                            />
                                        </template>
                                    </Map>
                                </Suspense>
                            </Teleport>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useLoungesStore } from '@/store/lounges';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import Icon from '@/components/ui/icon/icon.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import UiSingleSelect from '@/components/ui/inputs/single-select/ui-single-select.vue';
import LoungeFilterModal from '@/components/tools/lounges/lounges-search/lounge-filter-modal/lounge-filter-modal.vue';
import LoungeWizardSearch from '@/components/tools/lounges/lounge-wizard/lounge-wizard-search/lounge-wizard-search.vue';
import LoungesFilterBar from '@/components/tools/lounges/lounges-search/lounges-filter-bar/lounges-filter-bar.vue';
import LoungesSearchResults from '@/components/tools/lounges/lounges-search/lounges-search-results/lounges-search-results.vue';
import Map from '@/components/tools/lounges/partials/lounge-map/lounge-map.vue';
import LoungeSearchResultsHeader from '@/components/tools/lounges/partials/lounge-search-results-header/lounge-search-results-header.vue';
import LoungeInfoWindow from '@/components/tools/lounges/partials/lounge-infowindow/lounge-infowindow.vue';

export default {
    name: 'LoungeWizard',
    components: {
        EmptyState,
        Icon,
        LoadingState,
        LoungeInfoWindow,
        LoungeWizardSearch,
        LoungesFilterBar,
        LoungesSearchResults,
        Map,
        LoungeSearchResultsHeader,
        LoungeFilterModal,
        UiSingleSelect,
    },
    setup() {
        const loungesStore = useLoungesStore();
        const {
            activeTripLeg,
            error,
            loading,
            mapMarkers,
            mapType,
            mapCenter,
            searchResults,
            searchResultsCount,
            searchState,
            showMap,
            tripAirports,
            userFlights,
        } = storeToRefs(loungesStore);
        const {
            getFilterOptions,
            parseTripsFromUrl,
            searchWizard,
            setUserFlights,
            setActiveTripLeg,
            setMapType,
            setSearchType,
            setShowMap,
            setSearchState,
        } = loungesStore;

        const route = useRoute();
        const favoritesChanged = ref(false);

        const handleCloseMap = () => {
            if (favoritesChanged.value) {
                searchWizard();
            }
            setShowMap(false);
        };

        const handleFavoriteChanged = () => {
            favoritesChanged.value = true;
        };

        const changeTripLeg = async (airport) => {
            if (!airport) return;
            setActiveTripLeg(airport.ordinal);
            await searchWizard();
        };

        const localTripLeg = ref({});

        const toggleOpenMap = () => {
            const mapState = showMap.value;
            if (!mapState) {
                setMapType('searchResults');
            }
            setShowMap(!mapState);
        };

        const setLocalTripLeg = () => {
            if (tripAirports.value.airports.length) {
                localTripLeg.value = tripAirports.value.airports.find(
                    (a) => a.ordinal == activeTripLeg.value
                );
            }
        };

        onMounted(async () => {
            setSearchType('Trip wizard');
            await getFilterOptions();

            if (_.isEmpty(userFlights.value)) {
                const tripsData = parseTripsFromUrl(route.query);

                setUserFlights(tripsData.flights);
                setActiveTripLeg(tripsData.activeTripLeg || 1);

                await searchWizard();
            }
        });

        watch(
            [() => searchResults.value, () => error.value],
            ([newResults, newError]) => {
                if (newError) {
                    setSearchState('error');
                } else if (newResults === null) {
                    setSearchState('loading');
                } else if (searchResultsCount.value === 0) {
                    setSearchState('empty');
                } else {
                    setSearchState('results');
                }
            },
            { immediate: true }
        );

        watch([() => activeTripLeg.value, () => searchResults.value], () => {
            setLocalTripLeg();
        });

        return {
            changeTripLeg,
            error,
            handleCloseMap,
            handleFavoriteChanged,
            loading,
            localTripLeg,
            mapMarkers,
            mapType,
            mapCenter,
            toggleOpenMap,
            searchResultsCount,
            searchState,
            setShowMap,
            showMap,
            tripAirports,
            userFlights,
        };
    },
};
</script>
